// CodeTemplates.jsx

// Supervised Templates
const supervisedTemplates = {
    "data-preparation": `# Section: data-preparation
import pandas as pd
import numpy as np
import os
from sklearn.model_selection import train_test_split

def load_data(filepath):
    """Load data from CSV file."""
    try:
        df = pd.read_csv(filepath)

        Feature_1 = 'Feature_1' # Alter this value
        Feature_2 = 'Feature_2' # Alter this value 

        # MANDATORY: User must define features (X)
        X = df[['Feature_1', 'Feature_2']]

        Label = 'Label' # Alter this value

        # MANDATORY: User must define Y (label)
        Y = df['Label'] if 'Label' in df.columns else None

        test_size_1 = 0.3 # Alter this value
        random_state_1 = 42 # Alter this value

        test_size_2 = 0.5 # Alter this value
        random_state_2 = 42 # Alter this value

        # USER CAN ADD PREPROCESSING HERE

        # Cannot change anything below this line
        X_train, X_temp, Y_train, Y_temp = train_test_split(X, Y, test_size=test_size_1, random_state=random_state_1)
        X_val, X_test, Y_val, Y_test = train_test_split(X_temp, Y_temp, test_size=test_size_2, random_state=random_state_2)
        return (X_train, Y_train, X_val, Y_val, X_test, Y_test)
    except Exception as e:
        print(f"Error loading file: {e}")
        return None, None, None, None, None, None`,
    "model-definition": `# Section: model-definition
import tensorflow as tf
import pandas as pd
from tensorflow.keras.models import Sequential
from tensorflow.keras.layers import Dense
from io import StringIO
import sys

input_dim_val = 2
kernel_initializer_1 = 'uniform'
kernel_initializer_2 = 'uniform'
kernel_initializer_3 = 'uniform'
activation_val_1 = 'sigmoid'
activation_val_2 = 'sigmoid'
activation_val_3 = 'sigmoid'

def build_model(output_dir=None):
    """Define the DNN model."""
    model = Sequential([
        Dense(2, input_dim=input_dim_val, kernel_initializer=kernel_initializer_1, activation=activation_val_1),
        Dense(3, kernel_initializer=kernel_initializer_2, activation=activation_val_2),
        Dense(1, kernel_initializer=kernel_initializer_3, activation=activation_val_3),
        # ---
    ])

    # Cannot change anything below this
    summary_str = StringIO()
    original_stdout = sys.stdout
    sys.stdout = summary_str
    model.summary()
    sys.stdout = original_stdout
    with open(os.path.join(output_dir, 'model_summary_before_training.txt'), 'w') as f:
        f.write(summary_str.getvalue())

    return model`,
    "training-configuration": `# Section: training-configuration
from tensorflow.keras.optimizers import Adam

compile_loss = 'binary_crossentropy'
compile_optimizer = Adam(learning_rate=0.001)
compile_metrics = 'accuracy'
split1_test_size = 0.2
split1_random_state = 42
split2_test_size = 0.2
split2_random_state = 42

def train_model(model, data, labels, model_name, output_dir=None):
    """Train the model with the given data."""
    model.compile(loss=compile_loss, optimizer=compile_optimizer, metrics=[compile_metrics])

    if labels is not None:
        X_train, X_test, Y_train, Y_test = train_test_split(data, labels, test_size=split1_test_size, random_state=split1_random_state)
    else:
        X_train, X_test = train_test_split(data, test_size=split2_test_size, random_state=split2_random_state)
        Y_train, Y_test = None, None

    # Train the model
    history = model.fit(
        X_train, 
        Y_train,
        epochs=3,
        batch_size=32,
        verbose=1,
        validation_data=(X_test, Y_test) if Y_train is not None else None
    )

    return history, model, X_test, Y_test`,
    "evaluation": `# Section: evaluation
from sklearn.metrics import (
    confusion_matrix,
    precision_recall_fscore_support,
    roc_auc_score,
    accuracy_score
)
import numpy as np
import os
import csv

def evaluate_model(model, test_data, test_labels, history=None, output_dir=None):
    """
    Evaluate a supervised TensorFlow model.

    Supports:
    - Binary classification with sigmoid output
    - Multiclass classification with softmax output
    - Integer or one-hot encoded labels
    """
    evaluation_result = model.evaluate(
        test_data,
        test_labels,
        verbose=0
    )

    # Keras can return either a scalar loss or a list of metrics.
    if isinstance(evaluation_result, (list, tuple)):
        loss = float(evaluation_result[0])
        accuracy = (
            float(evaluation_result[1])
            if len(evaluation_result) > 1
            else None
        )
    else:
        loss = float(evaluation_result)
        accuracy = None

    prediction_scores = model.predict(
        test_data,
        verbose=0
    )

    prediction_scores = np.asarray(prediction_scores)
    true_labels = np.asarray(test_labels)

    # Convert one-hot encoded labels to class indices.
    if true_labels.ndim > 1 and true_labels.shape[-1] > 1:
        true_labels = np.argmax(true_labels, axis=1)
    else:
        true_labels = true_labels.reshape(-1)

    # Binary classification: one sigmoid output per sample.
    if (
        prediction_scores.ndim == 1
        or (
            prediction_scores.ndim == 2
            and prediction_scores.shape[1] == 1
        )
    ):
        positive_scores = prediction_scores.reshape(-1)
        predicted_labels = (
            positive_scores >= 0.5
        ).astype(int)

        average_method = "binary"

        auc_score = (
            roc_auc_score(true_labels, positive_scores)
            if len(np.unique(true_labels)) > 1
            else None
        )

    # Multiclass classification: one score per class.
    else:
        predicted_labels = np.argmax(
            prediction_scores,
            axis=1
        )

        # Macro gives equal importance to every class.
        average_method = "macro"

        try:
            auc_score = roc_auc_score(
                true_labels,
                prediction_scores,
                multi_class="ovr",
                average="macro"
            )
        except ValueError:
            auc_score = None

    calculated_accuracy = accuracy_score(
        true_labels,
        predicted_labels
    )

    precision, recall, f1, _ = (
        precision_recall_fscore_support(
            true_labels,
            predicted_labels,
            average=average_method,
            zero_division=0
        )
    )

    cm = confusion_matrix(
        true_labels,
        predicted_labels
    )

    metrics = {
        "loss": loss,
        "accuracy": float(
            accuracy
            if accuracy is not None
            else calculated_accuracy
        ),
        "precision": float(precision),
        "recall": float(recall),
        "f1_score": float(f1),
        "auc_roc": (
            float(auc_score)
            if auc_score is not None
            else None
        ),
        "confusion_matrix": cm.tolist()
    }

    # These printed values are captured by DEEBug's output parser.
    print(f"Evaluation Loss: {metrics['loss']:.4f}")
    print(f"Evaluation Accuracy: {metrics['accuracy']:.4f}")
    print(f"Precision: {metrics['precision']:.4f}")
    print(f"Recall: {metrics['recall']:.4f}")
    print(f"F1 Score: {metrics['f1_score']:.4f}")

    if metrics["auc_roc"] is not None:
        print(f"AUC ROC: {metrics['auc_roc']:.4f}")

    if output_dir:
        os.makedirs(output_dir, exist_ok=True)

        metrics_csv_filename = os.path.join(
            output_dir,
            "test_model_results.csv"
        )

        with open(
            metrics_csv_filename,
            "w",
            newline=""
        ) as file:
            writer = csv.writer(file)

            if history and hasattr(history, "history"):
                writer.writerow(["Epoch Metrics"])
                writer.writerow([
                    "epoch",
                    "train_loss",
                    "train_accuracy",
                    "val_loss",
                    "val_accuracy"
                ])

                history_loss = history.history.get(
                    "loss",
                    []
                )

                for epoch in range(len(history_loss)):
                    writer.writerow([
                        epoch + 1,
                        history_loss[epoch],
                        history.history.get(
                            "accuracy",
                            ["N/A"] * len(history_loss)
                        )[epoch],
                        history.history.get(
                            "val_loss",
                            ["N/A"] * len(history_loss)
                        )[epoch],
                        history.history.get(
                            "val_accuracy",
                            ["N/A"] * len(history_loss)
                        )[epoch]
                    ])

                writer.writerow([])

            writer.writerow([
                "Final Evaluation Metrics"
            ])
            writer.writerow(["Metric", "Value"])

            for metric, value in metrics.items():
                if metric != "confusion_matrix":
                    writer.writerow([metric, value])

            writer.writerow([])
            writer.writerow(["Confusion Matrix"])

            for row in metrics["confusion_matrix"]:
                writer.writerow(row)

    return metrics`,
    "visualization": `# Section: visualization
import matplotlib.pyplot as plt

plot1_history = 'accuracy'
plot1_label = 'Train Accuracy'
plot2_history = 'val_accuracy'
plot2_label = 'Val Accuracy'
plot3_history = 'loss'
plot3_label = 'Train Loss'
plot4_history = 'val_loss'
plot4_label = 'Val Loss'

def visualize_results(history, output_dir=None):
    """Visualize training results."""
    plt.figure()
    plt.plot(history.history[plot1_history], label=plot1_label)
    if 'val_accuracy' in history.history:
        plt.plot(history.history[plot2_history], label=plot2_label)
    plt.plot(history.history[plot3_history], label=plot3_label)
    if 'val_loss' in history.history:
        plt.plot(history.history[plot4_history], label=plot4_label)

    plt.title('Training & Validation Accuracy/Loss')
    plt.xlabel('Epoch')
    plt.ylabel('Value')
    plt.legend()
    plt.savefig(os.path.join(output_dir, 'training_plot.png'))
    plt.close()`,
};

// Unsupervised Templates
const unsupervisedTemplates = {
    "data-preparation": `# Section: data-preparation
import pandas as pd
import numpy as np
import os
from sklearn.model_selection import train_test_split

def load_data(filepath):
    """Load data from CSV file."""
    try:
        df = pd.read_csv(filepath)

        Feature_1 = 'Feature_1'
        Feature_2 = 'Feature_2'

        # MANDATORY: User must define features (X)
        X = df[[Feature_1, Feature_2]]

        # NO LABEL REQUIRED FOR UNSUPERVISED LEARNING
        Y = None

        # USER CAN ADD PREPROCESSING HERE

        # Cannot change anything below this
        X_train, X_temp = train_test_split(X, test_size=0.3, random_state=42)
        X_val, X_test = train_test_split(X_temp, test_size=0.5, random_state=42)
        return (X_train, X_val, X_test)
    except Exception as e:
        print(f"Error loading file: {e}")
        return None, None, None`,
    "model-definition": `# Section: model-definition
import tensorflow as tf
import pandas as pd
from tensorflow.keras.models import Sequential
from tensorflow.keras.layers import Dense
from io import StringIO
import sys

def build_model(output_dir=None):
    """Define the DNN model."""
    model = Sequential([
        Dense(4, input_dim=2, kernel_initializer='uniform', activation='relu'),
        Dense(3, kernel_initializer='uniform', activation='relu'),
        Dense(2, kernel_initializer='uniform', activation='sigmoid'),
        # Add new layers

        # FIXED LINES BELOW (User cannot change)
    ])

    summary_str = StringIO()
    original_stdout = sys.stdout
    sys.stdout = summary_str
    model.summary()
    sys.stdout = original_stdout
    with open(os.path.join(output_dir, 'model_summary_before_training.txt'), 'w') as f:
        f.write(summary_str.getvalue())

    return model`,
    "training-configuration": `# Section: training-configuration
from tensorflow.keras.optimizers import Adam

def train_model(model, data, labels, model_name, output_dir=None):
    """Train the model with the given data."""
    if labels is None:
        model.compile(loss='mse', optimizer=Adam(learning_rate=0.001), metrics=['mse'])
    else:
        model.compile(loss='binary_crossentropy', optimizer=Adam(learning_rate=0.001), metrics=['accuracy'])

    if labels is not None:
        X_train, X_test, Y_train, Y_test = train_test_split(data, labels, test_size=0.2, random_state=42)
    else:
        X_train, X_test = train_test_split(data, test_size=0.2, random_state=42)
        Y_train, Y_test = X_train, X_test

    # Train the model
    history = model.fit(
        X_train, 
        Y_train,
        epochs=3,
        batch_size=32,
        verbose=1,
        validation_data=(X_test, Y_test)
    )

    return history, model, X_test, Y_test`,
    "evaluation": `# Section: evaluation
from sklearn.metrics import mean_squared_error, mean_absolute_error
import numpy as np
import json
import csv

def evaluate_model(model, test_data, test_labels=None, history=None, output_dir=None):
    """Evaluate the trained unsupervised autoencoder model using reconstruction metrics."""
    if hasattr(test_data, 'values'):
        test_data = test_data.values

    reconstructed_data = model.predict(test_data, verbose=0)

    mse = mean_squared_error(test_data, reconstructed_data)
    mae = mean_absolute_error(test_data, reconstructed_data)
    rmse = np.sqrt(mse)

    total_variance = np.var(test_data.flatten())
    reconstruction_variance = np.var(reconstructed_data.flatten())
    explained_variance_ratio = reconstruction_variance / total_variance if total_variance != 0 else 0

    metrics = {
        'mean_squared_error': float(mse),
        'root_mean_squared_error': float(rmse),
        'mean_absolute_error': float(mae),
        'explained_variance_ratio': float(explained_variance_ratio)
    }

    metrics_csv_filename = os.path.join(output_dir, "test_model_results.csv")
    with open(metrics_csv_filename, 'w', newline='') as f:
        writer = csv.writer(f)

        if history and hasattr(history, 'history'):
            writer.writerow(['Training History'])
            writer.writerow(['epoch', 'loss', 'mse', 'val_loss', 'val_mse'])
            n_epochs = len(history.history['loss'])
            for epoch in range(n_epochs):
                writer.writerow([
                    epoch + 1,
                    history.history['loss'][epoch],
                    history.history['mse'][epoch],
                    history.history['val_loss'][epoch] if 'val_loss' in history.history else 'N/A',
                    history.history['val_mse'][epoch] if 'val_mse' in history.history else 'N/A'
                ])
            writer.writerow([])

        writer.writerow(['Evaluation Metrics'])
        writer.writerow(['Metric', 'Value'])
        for key, value in metrics.items():
            writer.writerow([key, value])

        writer.writerow([])
        writer.writerow(['Example Reconstructions (First 5 samples)'])
        writer.writerow(['Sample', 'Original', 'Reconstructed', 'Absolute Error'])
        for i in range(min(5, len(test_data))):
            orig = test_data[i]
            recon = reconstructed_data[i]
            error = np.abs(orig - recon)
            writer.writerow([
                f'Sample {i+1}',
                np.array2string(orig, precision=4),
                np.array2string(recon, precision=4),
                np.array2string(error, precision=4)
            ])

    return metrics`,
    "visualization": `# Section: visualization
import matplotlib.pyplot as plt

def visualize_results(history, output_dir=None):
    """Visualize training results."""
    plt.figure(figsize=(10, 5))

    plt.subplot(1, 2, 1)
    plt.plot(history.history['mse'], label='Train MSE')
    if 'val_mse' in history.history:
        plt.plot(history.history['val_mse'], label='Val MSE')
    plt.title('Training & Validation MSE')
    plt.xlabel('Epoch')
    plt.ylabel('Mean Squared Error')
    plt.legend()

    plt.subplot(1, 2, 2)
    plt.plot(history.history['loss'], label='Train Loss')
    if 'val_loss' in history.history:
        plt.plot(history.history['val_loss'], label='Val Loss')
    plt.title('Training & Validation Loss')
    plt.xlabel('Epoch')
    plt.ylabel('Loss')
    plt.legend()

    plt.tight_layout()
    plt.savefig(os.path.join(output_dir, 'training_plot.png'))
    plt.close()`,
};

// Dynamically find line number

// Stage Component

// ============================================
// PYTORCH SUPERVISED TEMPLATES
// ============================================

const pytorchSupervisedTemplates = {
  "data-preparation": `# Section: data-preparation (PyTorch)
import torch
from torch.utils.data import DataLoader, TensorDataset
import pandas as pd
import numpy as np
from sklearn.model_selection import train_test_split

def load_data(filepath):
    """Load and prepare data for PyTorch."""
    try:
        df = pd.read_csv(filepath)

        Feature_1 = 'Feature_1'  # Alter this value
        Feature_2 = 'Feature_2'  # Alter this value

        # MANDATORY: User must define features (X)
        X = df[['Feature_1', 'Feature_2']].values

        Label = 'Label'  # Alter this value

        # MANDATORY: User must define Y (label)
        Y = df['Label'].values if 'Label' in df.columns else None

        test_size_1 = 0.3  # Alter this value
        random_state_1 = 42  # Alter this value

        # Split data
        X_train, X_temp, Y_train, Y_temp = train_test_split(
            X, Y, test_size=test_size_1, random_state=random_state_1
        )
        X_val, X_test, Y_val, Y_test = train_test_split(
            X_temp, Y_temp, test_size=0.5, random_state=42
        )

        # Convert to PyTorch tensors
        X_train = torch.FloatTensor(X_train)
        X_val = torch.FloatTensor(X_val)
        X_test = torch.FloatTensor(X_test)
        Y_train = torch.LongTensor(Y_train)
        Y_val = torch.LongTensor(Y_val)
        Y_test = torch.LongTensor(Y_test)

        return (X_train, Y_train, X_val, Y_val, X_test, Y_test)
    except Exception as e:
        print(f"Error loading file: {e}")
        return None, None, None, None, None, None`,

  "model-definition-mlp": `# Section: model-definition (PyTorch MLP)
import torch
import torch.nn as nn

input_dim_val = 2
hidden_dim_1 = 64
hidden_dim_2 = 32
output_dim = 2

class MLPModel(nn.Module):
    """Multi-Layer Perceptron for classification."""

    def __init__(self):
        super(MLPModel, self).__init__()
        self.layers = nn.Sequential(
            nn.Linear(input_dim_val, hidden_dim_1),
            nn.ReLU(),
            nn.Dropout(0.2),
            nn.Linear(hidden_dim_1, hidden_dim_2),
            nn.ReLU(),
            nn.Dropout(0.2),
            nn.Linear(hidden_dim_2, output_dim)
        )

    def forward(self, x):
        return self.layers(x)

def build_model(output_dir=None):
    """Build and return the model."""
    model = MLPModel()

    # Save model summary
    if output_dir:
        with open(f"{output_dir}/model_summary_before_training.txt", 'w') as f:
            f.write(str(model))

    return model`,

  "model-definition-cnn": `# Section: model-definition (PyTorch CNN)
import torch
import torch.nn as nn

class CNNModel(nn.Module):
    """Convolutional Neural Network for image classification."""

    def __init__(self, num_classes=10):
        super(CNNModel, self).__init__()

        self.conv_layers = nn.Sequential(
            nn.Conv2d(1, 32, kernel_size=3, padding=1),
            nn.ReLU(),
            nn.MaxPool2d(2, 2),
            nn.Conv2d(32, 64, kernel_size=3, padding=1),
            nn.ReLU(),
            nn.MaxPool2d(2, 2),
            nn.Conv2d(64, 128, kernel_size=3, padding=1),
            nn.ReLU(),
            nn.MaxPool2d(2, 2)
        )

        self.fc_layers = nn.Sequential(
            nn.Flatten(),
            nn.Linear(128 * 3 * 3, 256),
            nn.ReLU(),
            nn.Dropout(0.5),
            nn.Linear(256, num_classes)
        )

    def forward(self, x):
        x = self.conv_layers(x)
        x = self.fc_layers(x)
        return x

def build_model(num_classes=10, output_dir=None):
    model = CNNModel(num_classes)
    if output_dir:
        with open(f"{output_dir}/model_summary_before_training.txt", 'w') as f:
            f.write(str(model))
    return model`,

  "training-configuration": `# Section: training-configuration (PyTorch)
import torch
import torch.nn as nn
import torch.optim as optim
from torch.utils.data import DataLoader, TensorDataset

learning_rate = 0.001
batch_size = 32
num_epochs = 10

def train_model(model, X_train, Y_train, X_val, Y_val, output_dir=None):
    """Train the PyTorch model."""

    # Create DataLoaders
    train_dataset = TensorDataset(X_train, Y_train)
    val_dataset = TensorDataset(X_val, Y_val)
    train_loader = DataLoader(train_dataset, batch_size=batch_size, shuffle=True)
    val_loader = DataLoader(val_dataset, batch_size=batch_size)

    # Loss and optimizer
    criterion = nn.CrossEntropyLoss()
    optimizer = optim.Adam(model.parameters(), lr=learning_rate)

    history = {'loss': [], 'accuracy': [], 'val_loss': [], 'val_accuracy': []}

    for epoch in range(num_epochs):
        # Training phase
        model.train()
        train_loss, correct, total = 0, 0, 0

        for inputs, labels in train_loader:
            optimizer.zero_grad()
            outputs = model(inputs)
            loss = criterion(outputs, labels)
            loss.backward()
            optimizer.step()

            train_loss += loss.item()
            _, predicted = outputs.max(1)
            total += labels.size(0)
            correct += predicted.eq(labels).sum().item()

        train_acc = correct / total

        # Validation phase
        model.eval()
        val_loss, val_correct, val_total = 0, 0, 0

        with torch.no_grad():
            for inputs, labels in val_loader:
                outputs = model(inputs)
                loss = criterion(outputs, labels)
                val_loss += loss.item()
                _, predicted = outputs.max(1)
                val_total += labels.size(0)
                val_correct += predicted.eq(labels).sum().item()

        val_acc = val_correct / val_total

        # Record history
        history['loss'].append(train_loss / len(train_loader))
        history['accuracy'].append(train_acc)
        history['val_loss'].append(val_loss / len(val_loader))
        history['val_accuracy'].append(val_acc)

        print(f"Epoch {epoch+1}/{num_epochs} - Loss: {train_loss/len(train_loader):.4f} - Acc: {train_acc:.4f} - Val Loss: {val_loss/len(val_loader):.4f} - Val Acc: {val_acc:.4f}")

    return history, model`,

  evaluation: `# Section: evaluation (PyTorch)
import torch
import numpy as np
from sklearn.metrics import (
    confusion_matrix,
    precision_recall_fscore_support,
    accuracy_score
)
import csv
import os

def evaluate_model(model, X_test, Y_test, history=None, output_dir=None):
    """
    Evaluate a supervised PyTorch classification model.

    Supports:
    - Binary classification with one output value
    - Multiclass classification with class logits
    """
    model.eval()

    with torch.no_grad():
        outputs = model(X_test)

        # Binary classification:
        # shape may be [samples] or [samples, 1].
        if (
            outputs.dim() == 1
            or (
                outputs.dim() == 2
                and outputs.shape[1] == 1
            )
        ):
            probabilities = torch.sigmoid(
                outputs.reshape(-1)
            )

            predictions = (
                probabilities >= 0.5
            ).long()

            average_method = "binary"

        # Multiclass classification:
        # choose the class with the largest logit.
        else:
            predictions = torch.argmax(
                outputs,
                dim=1
            )

            average_method = "macro"

    predicted_labels = (
        predictions
        .detach()
        .cpu()
        .numpy()
        .reshape(-1)
    )

    true_labels = (
        Y_test
        .detach()
        .cpu()
        .numpy()
    )

    # Convert one-hot labels if necessary.
    if true_labels.ndim > 1 and true_labels.shape[-1] > 1:
        true_labels = np.argmax(
            true_labels,
            axis=1
        )
    else:
        true_labels = true_labels.reshape(-1)

    accuracy = accuracy_score(
        true_labels,
        predicted_labels
    )

    precision, recall, f1, _ = (
        precision_recall_fscore_support(
            true_labels,
            predicted_labels,
            average=average_method,
            zero_division=0
        )
    )

    cm = confusion_matrix(
        true_labels,
        predicted_labels
    )

    metrics = {
        "accuracy": float(accuracy),
        "precision": float(precision),
        "recall": float(recall),
        "f1_score": float(f1),
        "confusion_matrix": cm.tolist()
    }

    # Printed output is parsed and saved by DEEBug.
    print(f"Evaluation Accuracy: {metrics['accuracy']:.4f}")
    print(f"Precision: {metrics['precision']:.4f}")
    print(f"Recall: {metrics['recall']:.4f}")
    print(f"F1 Score: {metrics['f1_score']:.4f}")

    if output_dir:
        os.makedirs(output_dir, exist_ok=True)

        results_path = os.path.join(
            output_dir,
            "test_model_results.csv"
        )

        with open(
            results_path,
            "w",
            newline=""
        ) as file:
            writer = csv.writer(file)

            writer.writerow(["Metric", "Value"])

            for key, value in metrics.items():
                if key != "confusion_matrix":
                    writer.writerow([key, value])

            writer.writerow([])
            writer.writerow(["Confusion Matrix"])

            for row in metrics["confusion_matrix"]:
                writer.writerow(row)

    return metrics`,
};

// ============================================
// PYTORCH UNSUPERVISED TEMPLATES
// ============================================

const pytorchUnsupervisedTemplates = {
  autoencoder: `# Section: Autoencoder (PyTorch)
import torch
import torch.nn as nn
import torch.optim as optim

latent_dim = 32

class Autoencoder(nn.Module):
    """Simple Autoencoder for dimensionality reduction."""

    def __init__(self, input_dim, latent_dim=32):
        super(Autoencoder, self).__init__()

        # Encoder
        self.encoder = nn.Sequential(
            nn.Linear(input_dim, 128),
            nn.ReLU(),
            nn.Linear(128, 64),
            nn.ReLU(),
            nn.Linear(64, latent_dim)
        )

        # Decoder
        self.decoder = nn.Sequential(
            nn.Linear(latent_dim, 64),
            nn.ReLU(),
            nn.Linear(64, 128),
            nn.ReLU(),
            nn.Linear(128, input_dim),
            nn.Sigmoid()
        )

    def forward(self, x):
        encoded = self.encoder(x)
        decoded = self.decoder(encoded)
        return decoded

    def encode(self, x):
        return self.encoder(x)

def train_autoencoder(model, train_loader, epochs=50, lr=0.001):
    criterion = nn.MSELoss()
    optimizer = optim.Adam(model.parameters(), lr=lr)

    for epoch in range(epochs):
        total_loss = 0
        for data in train_loader:
            inputs = data[0]
            optimizer.zero_grad()
            outputs = model(inputs)
            loss = criterion(outputs, inputs)
            loss.backward()
            optimizer.step()
            total_loss += loss.item()

        print(f"Epoch {epoch+1}/{epochs}, Loss: {total_loss/len(train_loader):.4f}")

    return model`,

  vae: `# Section: Variational Autoencoder (PyTorch)
import torch
import torch.nn as nn
import torch.optim as optim
import torch.nn.functional as F

latent_dim = 20

class VAE(nn.Module):
    """Variational Autoencoder."""

    def __init__(self, input_dim, latent_dim=20):
        super(VAE, self).__init__()

        # Encoder
        self.fc1 = nn.Linear(input_dim, 128)
        self.fc2 = nn.Linear(128, 64)
        self.fc_mu = nn.Linear(64, latent_dim)
        self.fc_logvar = nn.Linear(64, latent_dim)

        # Decoder
        self.fc3 = nn.Linear(latent_dim, 64)
        self.fc4 = nn.Linear(64, 128)
        self.fc5 = nn.Linear(128, input_dim)

    def encode(self, x):
        h = F.relu(self.fc1(x))
        h = F.relu(self.fc2(h))
        return self.fc_mu(h), self.fc_logvar(h)

    def reparameterize(self, mu, logvar):
        std = torch.exp(0.5 * logvar)
        eps = torch.randn_like(std)
        return mu + eps * std

    def decode(self, z):
        h = F.relu(self.fc3(z))
        h = F.relu(self.fc4(h))
        return torch.sigmoid(self.fc5(h))

    def forward(self, x):
        mu, logvar = self.encode(x)
        z = self.reparameterize(mu, logvar)
        return self.decode(z), mu, logvar

def vae_loss(recon_x, x, mu, logvar):
    BCE = F.binary_cross_entropy(recon_x, x, reduction='sum')
    KLD = -0.5 * torch.sum(1 + logvar - mu.pow(2) - logvar.exp())
    return BCE + KLD

def train_vae(model, train_loader, epochs=50, lr=0.001):
    optimizer = optim.Adam(model.parameters(), lr=lr)

    for epoch in range(epochs):
        total_loss = 0
        for data in train_loader:
            inputs = data[0]
            optimizer.zero_grad()
            recon, mu, logvar = model(inputs)
            loss = vae_loss(recon, inputs, mu, logvar)
            loss.backward()
            optimizer.step()
            total_loss += loss.item()

        print(f"Epoch {epoch+1}/{epochs}, Loss: {total_loss/len(train_loader):.4f}")

    return model`,
};

// Add this function to switch between frameworks

// Export everything needed
export {
    supervisedTemplates,           // KEEP
    unsupervisedTemplates,         // KEEP
    pytorchSupervisedTemplates,    // ADD
    pytorchUnsupervisedTemplates   // ADD
};
